"use client";

import type { CaseStudiesGridBlock } from "@mw/shared/payload-types";

import { Block } from "@/app/_blocks/Block";
import { CardCaseStudy } from "@components/cards/CardCaseStudy";
import { SwitcherButton, type SwitcherOption } from "@components/core/SwitcherButton";
import { MultiSelect, type MultiSelectOption } from "@components/core/MultiSelect";
import { Button } from "@components/core/Button";
import { Skeleton } from "@components/core/Skeleton";
import { useTranslations } from "next-intl";
import React, { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { Key } from "react-aria-components";
import { filterEntitiesArray } from "@utilities/payload";
import type { FetchCardsFilters, FetchCardsDataResult } from "./type";
import { defaultLocale } from "@mw/shared/i18n";
import { getDisplayCategorySlug } from "./categories";
import { fetchCardsClient } from "./fetch";
import { buildCardsWithIcons } from "./cards";

export interface BlockCaseStudiesGridProps extends CaseStudiesGridBlock, BlockCaseStudiesGridDataProps {
  locale: string;
  pinnedArticleIdsMap?: Record<string, string[]>;
  initialCategoryIndex?: number;
  categoryUrlBasePath?: string;
  fetchCards?: (locale: string, page: number, filters?: FetchCardsFilters) => Promise<FetchCardsDataResult>;
}

export interface CardProps {
  id: string;
  industry: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  iconHover?: React.ReactNode;
}

export interface BlockCaseStudiesGridDataProps {
  cards: CardProps[];
  arrowIcon: React.ReactNode;
  hasMore?: boolean;
}
interface CardsState {
  cards: CardProps[];
  page: number;
  hasMore: boolean;
}

interface SwitcherOptionWithSlug extends SwitcherOption {
  slug: string;
}

export function BlockCaseStudiesGrid({
  categories,
  industries,
  cards: initialCards,
  backgroundImages,
  arrowIcon,
  locale,
  hasMore: initialHasMore = false,
  pinnedArticleIdsMap,
  fetchCards = fetchCardsClient,
  initialCategoryIndex = 0,
  categoryUrlBasePath,
}: BlockCaseStudiesGridProps) {
  const tGlobals = useTranslations("ClientKeys.Globals");
  const t = useTranslations("ClientKeys.CaseStudiesGrid");

  // Categories and industries received from the block config (props passed from CMS are unstable, do not use in deps!)
  const switcherOptions: SwitcherOptionWithSlug[] = useMemo(
    () =>
      filterEntitiesArray(categories ?? []).map(category => ({
        id: category.id,
        label: category.shortTitle || category.title,
        slug: category.slug ?? "",
      })),
    [],
  );

  const industryOptions: MultiSelectOption[] = useMemo(
    () =>
      filterEntitiesArray(industries ?? []).map(industry => ({
        value: industry.id,
        label: industry.title,
      })),
    [],
  );

  // Filters state
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(initialCategoryIndex);
  const [selectedIndustries, setSelectedIndustries] = useState<Key[]>([]);
  const isInitialRender = useRef(true);

  const currentFilters = useMemo(() => {
    const filters: FetchCardsFilters = {};

    if (switcherOptions.length > 0) {
      filters.categoryId = switcherOptions[selectedCategoryIndex].id;
      if (pinnedArticleIdsMap) {
        const pinned = pinnedArticleIdsMap[filters.categoryId];
        if (pinned?.length) filters.pinnedArticleIds = pinned;
      }
    }

    if (selectedIndustries.length > 0) {
      filters.industryIds = selectedIndustries.map(String);
    }

    return filters;
  }, [selectedCategoryIndex, selectedIndustries, pinnedArticleIdsMap]);

  // Cards State
  const [cardsState, setCardsState] = useState<CardsState>({
    cards: initialCards,
    page: 1,
    hasMore: initialHasMore,
  });
  const [isPending, startTransition] = useTransition();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fetch cards when filters change
  useEffect(() => {
    // Avoid fetching cards when the component is first mounted
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    setIsLoadingMore(false);
    startTransition(async () => {
      const result = buildCardsWithIcons(await fetchCards(locale, 1, currentFilters));
      setCardsState({
        cards: result.cards,
        page: 1,
        hasMore: result.hasMore,
      });
    });
  }, [locale, currentFilters]);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    startTransition(async () => {
      const nextPage = cardsState.page + 1;
      const result = buildCardsWithIcons(await fetchCards(locale, nextPage, currentFilters));
      setCardsState({
        cards: [...cardsState.cards, ...result.cards],
        page: nextPage,
        hasMore: result.hasMore,
      });
      setIsLoadingMore(false);
    });
  };

  return (
    <Block name='case-studies-grid'>
      <div className='tw-overflow-visible tw-container'>
        {/* Filters row */}
        <div className='tw-mb-10 tw-flex tw-flex-col tw-items-end tw-gap-4 md:tw-flex-row md:tw-items-end md:tw-justify-between'>
          {/* Category tabs on the left */}
          {switcherOptions.length > 0 && (
            <div className='tw-w-full tw-overflow-hidden md:tw-w-auto md:tw-pl-6'>
              <SwitcherButton
                options={switcherOptions}
                selectedIndex={selectedCategoryIndex}
                onSelect={index => {
                  // Sync the selected category with the URL, use nice display variant of the slug if available
                  setSelectedCategoryIndex(index);
                  if (categoryUrlBasePath) {
                    const slug = switcherOptions[index]?.slug;
                    const displaySlug = getDisplayCategorySlug(slug);
                    const path =
                      locale === defaultLocale
                        ? `/${categoryUrlBasePath}/${displaySlug}`
                        : `/${locale}/${categoryUrlBasePath}/${displaySlug}`;
                    window.history.replaceState(window.history.state, "", path);
                  }
                }}
              />
            </div>
          )}

          {/* Industry multi-select on the right */}
          {industryOptions.length > 0 && (
            <div className='tw-flex tw-justify-end md:tw-ml-auto md:tw-pr-6'>
              <MultiSelect
                aria-label={t("filter_by_industry")}
                options={industryOptions}
                onChange={industries => setSelectedIndustries(industries as Key[])}
                placeholder={t("filter_by_industry")}
                size='md'
              />
            </div>
          )}
        </div>

        {/* Cards grid */}
        <div className='tw-grid tw-grid-cols-12 tw-gap-4'>
          {isPending && !isLoadingMore
            ? // Show skeleton cards when filtering
              Array.from({ length: 9 }).map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className='tw-col-span-full sm:tw-col-span-6 lg:tw-col-span-4 tw-opacity-80'
                >
                  <CardCaseStudy
                    category={<Skeleton className='tw-h-4 tw-w-24 tw-bg-black/10' />}
                    arrowIcon={arrowIcon}
                    icon={<Skeleton className='tw-h-12 tw-w-12 tw-rounded-full tw-bg-black/10' />}
                    description={
                      <div className='tw-flex tw-flex-col tw-gap-2'>
                        <Skeleton className='tw-h-4 tw-w-full tw-bg-black/10' />
                        <Skeleton className='tw-h-4 tw-w-3/4 tw-bg-black/10' />
                      </div>
                    }
                    noiseOpacity={0.3}
                    backgroundImage={null}
                  />
                </div>
              ))
            : cardsState.cards.map((card, index) => (
                <div key={card.id} className='tw-col-span-full sm:tw-col-span-6 lg:tw-col-span-4'>
                  <CardCaseStudy
                    category={card.industry}
                    arrowIcon={arrowIcon}
                    icon={card.icon}
                    iconHover={card.iconHover}
                    description={card.description}
                    href={card.href}
                    noiseOpacity={0.3}
                    backgroundImage={
                      backgroundImages && backgroundImages.length
                        ? backgroundImages[index % backgroundImages?.length].image
                        : null
                    }
                  />
                </div>
              ))}
        </div>

        {/* Empty state */}
        {cardsState.cards.length === 0 && !isPending && (
          <div className='tw-py-20 tw-text-center'>
            <p className='tw-type-sm tw-text-black/60'>{t("no_case_studies_found")}</p>
          </div>
        )}

        {/* Load More button */}
        {cardsState.hasMore && (!isPending || isLoadingMore) && (
          <div className='tw-mt-8 tw-flex tw-justify-center tw-pb-0.5'>
            <Button onClick={handleLoadMore} disabled={isLoadingMore} variant='elevated' size='lg'>
              {isLoadingMore ? tGlobals("loading") : tGlobals("Load More")}
            </Button>
          </div>
        )}
      </div>
    </Block>
  );
}

BlockCaseStudiesGrid.displayName = "BlockCaseStudiesGrid";
